import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { canon } from "@/lib/rollen";
import { ShieldPlus, ShieldCheck, Users, Wrench, Flag, Headphones, PhoneCall, HardHat, ClipboardList, Briefcase, Sparkles } from "lucide-react";

/**
 * WelkomOverlay — verschijnt eenmalig direct na login (Optie A privé-login).
 *
 * Toont initialen-avatar + naam + rol + korte samenvatting van rechten.
 * Auto-close na AUTO_CLOSE_MS ms of bij klik/tap ergens op de overlay.
 * State wordt bewaard in sessionStorage.bpt_welkom door Login.jsx.
 */
const AUTO_CLOSE_MS = 1200;

const ROLE_INFO = {
  manager_plus: {
    label: "COO — Operationeel Directeur",
    icon: ShieldPlus,
    rechten: "Volledige toegang · alle modules · superadmin",
    accent: "hsl(var(--bpt-red))",
  },
  directie: {
    label: "Directie",
    icon: ShieldPlus,
    rechten: "Volledige toegang · alle modules · superadmin",
    accent: "hsl(var(--bpt-red))",
  },
  manager: {
    label: "Manager",
    icon: ShieldCheck,
    rechten: "HR · assets · financieel · documenten",
    accent: "hsl(var(--brand-blue))",
  },
  manager_operationeel: {
    label: "Manager Operationeel",
    icon: ShieldCheck,
    rechten: "Operationele aansturing · planning · monteurs",
    accent: "hsl(var(--brand-blue))",
  },
  manager_bedrijfsvoering: {
    label: "Manager Bedrijfsvoering",
    icon: Briefcase,
    rechten: "Financieel · cashflow · offertes · facturen",
    accent: "hsl(var(--brand-blue))",
  },
  manager_brandveiligheid: {
    label: "Manager Brandveiligheid",
    icon: ShieldCheck,
    rechten: "REOB · certificaten · keuringen · plannen",
    accent: "hsl(var(--brand-blue))",
  },
  teammanager: {
    label: "Teammanager",
    icon: Users,
    rechten: "Klanten · offertes · tickets · team-view",
    accent: "hsl(var(--brand-blue))",
  },
  kcc: {
    label: "KCC",
    icon: Headphones,
    rechten: "Kantoor · planning · backoffice",
    accent: "#0f766e",
  },
  buitendienst: {
    label: "Buitendienst",
    icon: Wrench,
    rechten: "Werkbonnen · keuringen · certificaten",
    accent: "#0f766e",
  },
  buitendienst_nl: {
    label: "Buitendienst Nederland",
    icon: Wrench,
    rechten: "Werkbonnen · keuringen · certificaten (NL)",
    accent: "#0f766e",
  },
  buitendienst_be: {
    label: "Buitendienst België",
    icon: Flag,
    rechten: "Werkbonnen · keuringen · Apragaz (BE)",
    accent: "#0f766e",
  },
  salesteam: {
    label: "Salesteam",
    icon: PhoneCall,
    rechten: "Tickets · offertes · klantcontact",
    accent: "#7c3aed",
  },
  projectleider_pb: {
    label: "Projectleider BB",
    icon: ClipboardList,
    rechten: "Projecten PB · applicateurs · overdrachten",
    accent: "#7c3aed",
  },
  applicateur: {
    label: "Applicateur (ZZP)",
    icon: HardHat,
    rechten: "Doorvoeringen · Mulcol · projecten PB",
    accent: "#7c3aed",
  },
  medewerker: {
    label: "Medewerker",
    icon: Sparkles,
    rechten: "Basis-toegang · eigen taken en tickets",
    accent: "#64748b",
  },
};

function initialsFrom(name) {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function WelkomOverlay() {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (!user) return;
    let raw = null;
    try { raw = sessionStorage.getItem("bpt_welkom"); } catch {}
    if (!raw) return;
    let parsed;
    try { parsed = JSON.parse(raw); } catch { return; }
    // Alleen tonen als recent gemarkeerd (binnen 15 sec)
    if (!parsed || !parsed.shownAt || Date.now() - parsed.shownAt > 15000) {
      sessionStorage.removeItem("bpt_welkom");
      return;
    }
    setVisible(true);
    const t = setTimeout(() => close(), AUTO_CLOSE_MS);
    return () => clearTimeout(t);
  }, [user]);

  const close = () => {
    setClosing(true);
    try { sessionStorage.removeItem("bpt_welkom"); } catch {}
    setTimeout(() => setVisible(false), 320);
  };

  if (!visible || !user) return null;

  const info = ROLE_INFO[canon(user.role)] || ROLE_INFO.medewerker;
  const Icon = info.icon;
  const inits = initialsFrom(user.name);

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center px-4 transition-opacity duration-300 ${closing ? "opacity-0" : "opacity-100"}`}
      onClick={close}
      role="dialog"
      aria-label="Welkom overlay"
      data-testid="welkom-overlay"
      style={{ background: "rgba(15, 23, 42, 0.72)", backdropFilter: "blur(6px)" }}
    >
      <div
        className={`relative bg-white rounded-2xl shadow-2xl overflow-hidden max-w-md w-full transition-transform duration-300 ${closing ? "scale-95" : "scale-100"}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Kleur-accent bovenaan */}
        <div style={{ height: 6, background: info.accent }} />

        <div className="p-5 sm:p-8 text-center">
          {/* Initialen-avatar */}
          <div
            className="mx-auto h-16 w-16 sm:h-20 sm:w-20 rounded-full flex items-center justify-center text-white text-xl sm:text-2xl font-heading font-bold shadow-md ring-4 ring-white"
            style={{ background: info.accent }}
            data-testid="welkom-avatar"
          >
            {inits}
          </div>

          <p className="mt-3 text-[10px] sm:text-xs uppercase tracking-widest text-slate-400">Welkom terug</p>
          <h2 className="mt-1 font-heading text-xl sm:text-3xl font-black tracking-tighter text-slate-900" data-testid="welkom-name">
            {user.name}
          </h2>

          <div
            className="mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs sm:text-sm font-semibold text-white"
            style={{ background: info.accent }}
            data-testid="welkom-rol"
          >
            <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            {info.label}
          </div>

          <p className="mt-3 text-xs sm:text-sm text-slate-600 leading-relaxed">
            {info.rechten}
          </p>

          <button
            onClick={close}
            className="mt-4 w-full sm:w-auto sm:mt-6 px-4 py-2 rounded-lg text-xs font-semibold bg-slate-900 text-white active:bg-slate-700"
            data-testid="welkom-sluiten"
          >
            Tik om verder te gaan
          </button>
        </div>
      </div>
    </div>
  );
}

export { ROLE_INFO, initialsFrom };
